import React, { Component } from 'react';
import { Card, Form, Badge, Button, ButtonGroup, ToggleButton, Row, Col, Modal, Table, Alert } from 'react-bootstrap';
import { GoX, GoPlus, GoArrowUp } from "react-icons/go";
import { GrDocument } from "react-icons/gr";
import { LuTableProperties, LuComponent } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import Tooltip from "../../controls/common/tooltip";
import AceEditor from "react-ace";
import Utils from "context/utils";

// Modal used to edit value-typed session-object envelopes:
//   - { type: "message", message: { role, content, attributes, providers, metadata } }
//   - { type: "thread",  thread:  { attributes, metadata } }
//   - { type: "graph",   graph:   { attributes, metadata } }
//
// The "envelope-only" variants (thread, graph) expose just attributes +
// metadata — content/role/providers don't apply. Everything else (root header,
// JSON field editor, validation, save) is shared.
export default class EditMessageContentModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      portValue: null,
      viewOption: "content",
      fields: {},
      errors: {}
    };

    this.messageViewOptions = [
      { label: <GrDocument/>, value: "content", tooltip: "Content" },
      { label: <LuTableProperties/>, value: "attributes", tooltip: "Attributes" },
      { label: <LuComponent/>, value: "providers", tooltip: "Providers" },
      { label: <CgDetailsMore/>, value: "metadata", tooltip: "Metadata" },
    ];

    this.threadViewOptions = [
      { label: <LuTableProperties/>, value: "attributes", tooltip: "Attributes" },
      { label: <CgDetailsMore/>, value: "metadata", tooltip: "Metadata" },
    ];
  }

  // The key under which the envelope's payload lives — "message", "thread", or
  // "graph". Unknown/absent types fall back to "message".
  innerKeyFor(value) {
    if ( value && (value.type === "thread" || value.type === "graph") ) {
      return value.type;
    }
    return "message";
  }

  getInnerKey() {
    return this.innerKeyFor(this.state.portValue);
  }

  // Envelope-only value types (thread, graph) carry just attributes + metadata —
  // no message content / role / providers.
  isEnvelopeOnly() {
    const key = this.getInnerKey();
    return key === "thread" || key === "graph";
  }

  // Capitalized label for the active type: "Message" / "Thread" / "Graph".
  typeLabel() {
    const key = this.getInnerKey();
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  getViewOptions() {
    return this.isEnvelopeOnly() ? this.threadViewOptions : this.messageViewOptions;
  }

  // Initial JSON-field set: envelope-only types (thread, graph) have just
  // attributes + metadata; message has those plus providers.
  fieldKeysFor(value) {
    const key = this.innerKeyFor(value);
    return ( key === "thread" || key === "graph" )
      ? ["attributes", "metadata"]
      : ["attributes", "providers", "metadata"];
  }

  createFieldValue(field) {
    const key = this.innerKeyFor(this.props.portValue);
    return this.props.portValue[key][field]
      ? JSON.stringify(this.props.portValue[key][field], null, 2) : "";
  }

  setError(error, message) {
    this.state.errors[error] = message;
    this.setState({errors: this.state.errors});
  }

  clearError(error) {
    delete this.state.errors[error];
    this.setState({errors: this.state.errors});
  }

  // A message value may carry only metadata (no content) — e.g. a config object
  // stored under message.metadata. The content editor maps/splices over content,
  // so ensure a content array exists (metadata and the rest of the value are kept).
  ensureMessageContent(portValue) {
    if ( this.innerKeyFor(portValue) === "message" ) {
      if ( !portValue.message ) {
        portValue.message = {};
      }
      if ( !Array.isArray(portValue.message.content) ) {
        portValue.message.content = [];
      }
    }
    return portValue;
  }

  onEnter = () => {
    const portValue = this.ensureMessageContent(Utils.deepCopy(this.props.portValue));
    const fieldKeys = this.fieldKeysFor(this.props.portValue);
    const fields = {};
    for ( const k of fieldKeys ) {
      fields[k] = this.createFieldValue(k);
    }
    const isEnvelopeOnly = this.innerKeyFor(this.props.portValue) !== "message";
    this.setState({
      portValue: portValue,
      fields: fields,
      errors: {},
      // Envelope-only types (thread, graph) have no "content" tab — default to attributes.
      viewOption: isEnvelopeOnly ? "attributes" : "content"
    });
  }

  handleUpdate = () => {
    // Check if custom save handler is provided
    if ( this.props.onSave ) {
      this.props.onSave(this.state.portValue);
      this.props.onHide();
      return;
    }

    // Update the envelope payload (message, thread, or graph) on the original
    // portValue so the parent component picks up the changes.
    const key = this.innerKeyFor(this.props.portValue);
    this.props.portValue[key] = this.state.portValue[key];
    this.props.shell.shellInterface.markModified();

    // Close modal
    this.props.onHide();
  }

  /**
   * Root Properties
   */

  renderRootProperties() {
    const key = this.getInnerKey();
    const payload = this.state.portValue[key] || {};
    const isEnvelopeOnly = this.isEnvelopeOnly();

    let createdAt = "n/a";
    if ( payload["attributes"] && payload["attributes"]["created_at"] ) {
      createdAt = Utils.formatDateTime(payload["attributes"]["created_at"]);
    }

    return (
      <Row style={{"margin-bottom": "12px"}}>
        <Col sm={"auto"} className="d-flex align-items-center">
          <span className="text-muted" style={{"font-size": "14px"}}>
            Type
          </span>
          &nbsp;&nbsp;
          <Badge variant="secondary" className="title-tag-badge">
            {key}
          </Badge>
          {!isEnvelopeOnly &&
            <>
              &nbsp;&nbsp;
              <span className="text-muted" style={{"font-size": "14px"}}>
                Role
              </span>
              &nbsp;&nbsp;
              <Badge variant="secondary" className="title-tag-badge">
                {payload["role"]}
              </Badge>
            </>
          }
          &nbsp;&nbsp;
          <span className="text-muted" style={{"font-size": "14px"}}>
            Time
          </span>
          &nbsp;&nbsp;
          <Badge variant="secondary" className="title-tag-badge">
            {createdAt}
          </Badge>
        </Col>
        <Col>
          <span className="float-right">
            <ButtonGroup toggle size="sm">
              {this.getViewOptions().map((option, index) => (
                <Tooltip text={option.tooltip}>
                  <ToggleButton
                    key={index}
                    type="radio"
                    variant="light"
                    name="radio"
                    value={option.value}
                    checked={this.state.viewOption === option.value}
                    onChange={(event) => { this.setState({ viewOption: event.currentTarget.value }) }}
                  >
                    {option.label}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ButtonGroup>
          </span>
        </Col>
      </Row>
    );
  }

  /**
   * Content Editor
   */

  handleInsertContentItem(index) {
    // Content editor is message-only.
    this.state.portValue.message.content.splice(index, 0, {
      "type": "text",
      "text": ""
    });
    this.setState({ portValue: this.state.portValue });
  }

  handleAddTextContentItem() {
    this.handleInsertContentItem(this.state.portValue.message.content.length);
  }

  handleRemoveContentItem(index) {
    this.state.portValue.message.content.splice(index, 1);
    this.setState({ portValue: this.state.portValue });
  }

  renderContent() {
    let mode = "text";
    if ( this.state.portValue.message.attributes && this.state.portValue.message.attributes["syntax_mode"] ) {
      mode = this.state.portValue.message.attributes["syntax_mode"];
    }
    if ( new Set(["text", "markdown", "json", "yaml", "python"]).has(mode) === false ) {
      mode = 'text';
    }

    return (
      <>
        <Table hover bordered>
          {((this.state.portValue.message && this.state.portValue.message.content) || []).map((contentItem, index) => {
            return (
              <tr>
                <td>
                  <div>
                    <Form.Row className="align-items-center" style={{ "margin-bottom": "10px" }}>
                      <Col xs="auto">
                        <span className="text-muted" style={{"font-size": "12px"}}>
                          Type
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Badge variant="secondary" className="title-tag-badge">
                          text
                        </Badge>
                      </Col>
                      <Col>
                        <span className="float-right">
                          {this.props.shell.shellInterface.canEditRevision() &&
                            <>
                              <Tooltip text={"Insert new content item of \"text\" type above this one"}>
                                <Button variant="light" size="sm" onClick={() => this.handleInsertContentItem(index)}>
                                  <GoPlus /><GoArrowUp />
                                </Button>
                              </Tooltip>
                              &nbsp;&nbsp;
                              <Tooltip text="Remove content item">
                                <Button variant="light" size="sm" onClick={() => this.handleRemoveContentItem(index)}>
                                  <GoX />
                                </Button>
                              </Tooltip>
                            </>
                          }
                        </span>
                      </Col>
                    </Form.Row>
                    <Form.Row className="align-items-center">
                      <Col>
                        <AceEditor
                          name="message-content-item-editor"
                          width="100%"
                          mode={mode}
                          theme="monokai"
                          wrapEnabled="true"
                          readOnly={!this.props.shell.shellInterface.canEditRevision()}
                          value={contentItem.text}
                          minLines={3}
                          maxLines={55}
                          setOptions={{
                            "printMarginColumn": 120
                          }}
                          onChange={(newValue) => {
                            contentItem.text = newValue;
                          }}
                        />
                      </Col>
                    </Form.Row>
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>

        {this.props.shell.shellInterface.canEditRevision() &&
          <>
            <Button variant="success" size="sm" onClick={() => this.handleAddTextContentItem()}>
              Add Text
            </Button>
            <span className="text-muted text-note">
              &nbsp;&nbsp;&nbsp;Add new content item of "text" type
            </span>
          </>
        }
      </>
    );
  }

  /**
   * JSON fields (attributes, providers, metadata) — shared by message and thread.
   */

  handleAddField(field) {
    const key = this.getInnerKey();
    this.state.portValue[key][field] = {};
    this.state.fields[field] = "{}";
    this.setState({
      portValue: this.state.portValue,
      fields: this.state.fields
    });
  }

  handleUpdateField(field, newValue) {
    // Update text value
    this.state.fields[field] = newValue;
    this.setState({fields: this.state.fields});

    // Update serialized object
    try {
      const key = this.getInnerKey();
      this.state.portValue[key][field] = JSON.parse(newValue);
      this.setState({portValue: this.state.portValue});
      this.clearError(`${field}_invalid_json`);
    } catch (error) {
      this.setError(`${field}_invalid_json`, `\"${field}\" field contains invalid JSON`);
    }
  }

  handleRemoveField(field) {
    const key = this.getInnerKey();
    delete this.state.portValue[key][field];
    delete this.state.fields[field];
    this.setState({
      portValue: this.state.portValue,
      fields: this.state.fields
    });
  }

  renderField(field) {
    const key = this.getInnerKey();
    const payload = this.state.portValue[key];
    const noun = this.typeLabel();
    if ( !payload[field] ) {
      return (
        <>
          <Row>
            <Col style={{"padding-right": "0px"}}>
              <Alert variant="info">
                {noun} "{field}" field is not specified.
              </Alert>
            </Col>
            {this.props.shell.shellInterface.canEditRevision() &&
              <Col sm="auto">
                <Button variant="success" size="sm" onClick={() => this.handleAddField(field)}>
                  Add
                </Button>
              </Col>
            }
          </Row>
        </>
      );
    }
    return (
      <>
        <Form.Row className="align-items-center" style={{ "margin-bottom": "10px" }}>
          <Col xs="auto">
            <span className="text-muted" style={{"font-size": "12px"}}>
              Field
            </span>
          </Col>
          <Col xs="auto">
            <Badge variant="secondary" className="title-tag-badge">
              {field}
            </Badge>
          </Col>
          <Col>
            <span className="float-right">
              {this.props.shell.shellInterface.canEditRevision() &&
                <Tooltip text={`Remove \"${field}\" field`}>
                  <Button variant="light" size="sm" onClick={() => this.handleRemoveField(field)}>
                    <GoX />
                  </Button>
                </Tooltip>
              }
            </span>
          </Col>
        </Form.Row>

        <Card.Text>
          <AceEditor
            name={`playground-widget-${noun.toLowerCase()}-${field}`}
            width="100%"
            mode="json"
            theme="monokai"
            wrapEnabled="true"
            readOnly={!this.props.shell.shellInterface.canEditRevision()}
            value={this.state.fields[field]}
            onChange={(newValue) => this.handleUpdateField(field, newValue)}
            minLines={3}
            maxLines={30}
          />
        </Card.Text>
      </>
    );
  }

  /**
   * Main Renderer
   */

  render() {
    const hasError = Object.keys(this.state.errors).length > 0;

    const isEnvelopeOnly = this.isEnvelopeOnly();
    const title = "Edit " + this.typeLabel();

    return (
      <Modal
        {...this.props}
        onEnter={this.onEnter}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.portValue &&
            <>
              {this.renderRootProperties()}

              {!isEnvelopeOnly &&
                <div className={"content" === this.state.viewOption ? "" : "hidden"}>
                  {this.renderContent()}
                </div>
              }
              <div className={"attributes" === this.state.viewOption ? "" : "hidden"}>
                <Table bordered hover>
                  <tbody><tr><td>
                    {this.renderField("attributes")}
                  </td></tr></tbody>
                </Table>
              </div>
              {!isEnvelopeOnly &&
                <div className={"providers" === this.state.viewOption ? "" : "hidden"}>
                  <Table bordered hover>
                    <tbody><tr><td>
                      {this.renderField("providers")}
                    </td></tr></tbody>
                  </Table>
                </div>
              }
              <div className={"metadata" === this.state.viewOption ? "" : "hidden"}>
                <Table bordered hover>
                  <tbody><tr><td>
                    {this.renderField("metadata")}
                  </td></tr></tbody>
                </Table>
              </div>
            </>
          }
        </Modal.Body>
        <Modal.Footer>
          {hasError &&
            <span style={{"padding-right": "10px"}}>
              <Alert variant="warning">
                {this.state.errors[Object.keys(this.state.errors)[0]]}
              </Alert>
            </span>
          }

          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          {this.props.shell.shellInterface.canEditRevision() &&
            <Button variant="success" disabled={hasError} onClick={this.handleUpdate}>Apply</Button>
          }
        </Modal.Footer>
      </Modal>
    );
  }
}
